/**
 * MessagingContext - PostgreSQL-Only Version
 * Provides unread message count from backend (no Supabase dependency)
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getUnreadCount, MessagingRequestError } from '../services/messagingServiceV2';
import { hasCredentialReadyAuth } from '../services/api';
import { debugLog, LOG_PREFIX } from '../constants/messaging';

interface MessagingContextType {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
  markConversationRead: (requestId: number) => void;
}

const MessagingContext = createContext<MessagingContextType>({
  unreadCount: 0,
  refreshUnreadCount: async () => {},
  markConversationRead: () => {},
});

export const useMessagingNotifications = () => useContext(MessagingContext);

interface MessagingProviderProps {
  children: React.ReactNode;
  userId: string | number | undefined;
}

export const MessagingProvider: React.FC<MessagingProviderProps> = ({ children, userId }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const isMountedRef = useRef(true);
  // A rejected credential pauses network polling without permanently latching
  // the same user out. The normal interval keeps checking local credential
  // readiness and retries the server after this bounded backoff.
  const nextAuthRetryAtRef = useRef(0);

  // A different account must not inherit a previous credential backoff.
  useEffect(() => {
    setUnreadCount(0);
    nextAuthRetryAtRef.current = 0;
  }, [userId]);

  // Fetch total unread count from PostgreSQL backend
  const refreshUnreadCount = useCallback(async () => {
    if (!userId || !hasCredentialReadyAuth()) {
      setUnreadCount(0);
      return;
    }
    if (Date.now() < nextAuthRetryAtRef.current) return;

    try {
      const count = await getUnreadCount(String(userId));
      nextAuthRetryAtRef.current = 0;

      if (isMountedRef.current) {
        // The badge is server truth. Opening the inbox alone must not mark
        // unrelated conversations as read; getConversation handles the one
        // thread the user actually opens.
        setUnreadCount(count);
        debugLog(LOG_PREFIX.SUCCESS, `Total unread messages: ${count}`);
      }
    } catch (error) {
      const status = error instanceof MessagingRequestError
        ? error.status
        : (error as { status?: number } | null)?.status;
      if (status === 401 || status === 403) {
        // Avoid a 401/403 request every ten seconds, but retry after one minute
        // so silent token renewal for the same user can recover automatically.
        nextAuthRetryAtRef.current = Date.now() + 60_000;
        setUnreadCount(0);
      }
      debugLog(LOG_PREFIX.ERROR, 'Error fetching unread count:', error);
    }
  }, [userId]);

  // Mark a conversation as read (just refresh count)
  const markConversationRead = useCallback((requestId: number) => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  // Poll for unread count periodically (optimized with visibility API)
  useEffect(() => {
    if (!userId) return;

    let pollInterval: NodeJS.Timeout | null = null;

    const startPolling = () => {
      if (pollInterval) return;
      debugLog(LOG_PREFIX.SUBSCRIPTION, 'Starting unread count polling (10s)');
      pollInterval = setInterval(() => {
        refreshUnreadCount();
      }, 10000);
    };

    const stopPolling = () => {
      if (pollInterval) {
        debugLog(LOG_PREFIX.SUBSCRIPTION, 'Pausing polling (tab hidden)');
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadCount();
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Initial fetch
    refreshUnreadCount();

    if (document.visibilityState === 'visible') {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      debugLog(LOG_PREFIX.SUBSCRIPTION, 'Stopping unread count polling');
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, refreshUnreadCount]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <MessagingContext.Provider value={{ unreadCount, refreshUnreadCount, markConversationRead }}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingContext;
